import React, { useCallback, useEffect, useRef, useState } from 'react';
import { deviceStatusText, devicesForUser, loadAdminDevices, signOutAdminDevice } from '../features/users/adminDevices';

/**
 * The phones that remember this Admin's unlock (30 days, utils/adminRemember.js), with Sign out
 * (desktop User Management, an Admin user's details). Reads and actions: features/users/adminDevices.js.
 * Advisory: a failed read says so and never blocks the page around it.
 */
export default function RememberedPhones({ userId, userName }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const shown = useRef(userId);
  shown.current = userId;

  const load = useCallback(async () => {
    const r = await loadAdminDevices({ includeEnded: true });
    if (shown.current !== userId) return; // another user was opened meanwhile
    setUnavailable(r.unavailable);
    if (r.ok) {
      setRows(devicesForUser(r.rows, userId));
      setError(null);
    } else {
      setRows([]);
      setError(r.needsPin ? 'Unlock admin on this page to see remembered phones.' : `Couldn't read the remembered phones: ${r.message}`);
    }
  }, [userId]);

  useEffect(() => {
    setRows(null);
    if (userId) load();
  }, [userId, load]);

  const signOut = async (d) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Sign ${userName || 'this admin'} out on "${d.label}"? That phone will ask for the Admin PIN next time.`)) return;
    setBusyId(d.device_id);
    const r = await signOutAdminDevice(d.device_id);
    setBusyId(null);
    if (!r.ok) setError(`Couldn't sign that phone out: ${r.message}`);
    load();
  };

  if (unavailable) return null; // this JobQ doesn't remember phones (yet)
  const live = (rows || []).filter((d) => d.live);
  const ended = (rows || []).filter((d) => !d.live);
  return (
    <div className="mt-4 p-3 border rounded" data-testid="remembered-phones">
      <h5 className="mb-1">Remembered phones</h5>
      <div className="small text-muted mb-2">
        Phones that keep {userName ? `${userName}'s` : 'this'} admin unlock for 30 days. A new PIN or making the user inactive signs them all out.
        {' '}A lost phone: sign it out here <strong>and</strong> change the PIN.
      </div>
      {error && <div className="alert alert-warning py-2 small">{error}</div>}
      {rows === null && <div className="small text-muted">Loading…</div>}
      {rows && live.length === 0 && !error && <div className="small text-muted">No phone remembers this admin's unlock.</div>}
      {live.map((d) => (
        <div key={d.device_id} className="d-flex align-items-center gap-2 border-top py-2" data-testid={`remembered-phone-${d.device_id}`}>
          <div className="flex-grow-1">
            <div className="fw-semibold">{d.label}</div>
            <div className="small text-muted">{deviceStatusText(d)}</div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger" disabled={busyId === d.device_id} onClick={() => signOut(d)}>
            {busyId === d.device_id ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      ))}
      {ended.length > 0 && (
        <details className="mt-2 small">
          <summary className="text-muted">Ended in the last 30 days ({ended.length})</summary>
          {ended.map((d) => (
            <div key={d.device_id} className="text-muted py-1">{d.label}: {deviceStatusText(d)}</div>
          ))}
        </details>
      )}
    </div>
  );
}
